/* eslint-disable no-mixed-operators */
import { IGroup, Providers } from "@microsoft/mgt-react";
import { addAliasDomain } from "../utils/converter";
import { IPersonaProps } from "@fluentui/react";
import { PrincipalType } from "../core/components/PeoplePicker/types";
import Helper from "../core/libraries/Helper";
import { getCacheGroup, getCacheUser } from "../services/indexedDBServices";
import Constants from "../core/libraries/Constants";

async function getMe() {
    try {
        return await Providers.globalProvider.graph.client.api("me").get();
    } catch (error) {
        console.error(error);
        return undefined
    }
};

// /me/memberOf died because we dont have permission user.read


async function checkAmInGroups(groupIds: string[]) {

    try {
        return await Providers.globalProvider.graph.client.api("/me/checkMemberGroups").post({
            "groupIds": groupIds
        });
    } catch (error) {
        console.error(error);
        return undefined
    }
}

async function getGroupInfoById(groupId: string) {

    try {
        const user = Providers.globalProvider.getActiveAccount!();
        if (!!user && !!user.id) {
            const cachedGroup = await getCacheGroup(user.id, groupId)
            if (!!cachedGroup) {
                return JSON.parse(cachedGroup) as IGroup;
            }
        }
        return await Providers.globalProvider.graph.client.api(`/groups/${groupId}`).get();
    } catch (error) {
        console.error(error);
        return undefined
    }
}

async function getUserInfoById(userId: string) {

    try {
        const user = Providers.globalProvider.getActiveAccount!();
        if (!!user && !!user.id) {
            const cachedUser = await getCacheUser(user.id, addAliasDomain(userId))
            if (!!cachedUser) {
                return JSON.parse(cachedUser) as IGroup;
            }
        }
        return await Providers.globalProvider.graph.client.api(`/users/${addAliasDomain(userId)}`).get();
    } catch (error) {
        console.error(error);
        return undefined
    }
}

export async function getUserInfoByIds(userIds: string[]): Promise<IPersonaProps[] | undefined> {

    try {
        const results: IPersonaProps[] = []
        if (userIds.length > 0) {
            for (let index = 0; index < userIds.length; index++) {
                const userId = userIds[index];
                let name = await getUserInfoById(userId);
                !!name && !!name.id && results.push({
                    text: name.displayName,
                    secondaryText: !!name.userPrincipalName ? name.userPrincipalName : "",
                    tertiaryText: name.id,
                    showSecondaryText: true
                })
            }
        }
        return results;
    } catch (error) {
        console.error(error);
        return undefined
    }
}

export async function getDisplayNameOfUsers(objectIds: string[]) {
    let results: IPersonaProps[] = []
    try {
        if (objectIds.length > 0) {
            for (let index = 0; index < objectIds.length; index++) {
                const userId = objectIds[index];
                let name = await Providers.globalProvider.graph.client.api(`/users/${addAliasDomain(userId)}`).select("displayName").get();
                results.push(name)
            }
        }
    } catch (error) {
        console.error(error);
    }
    finally {
        return results
    }
}


async function queryPeopleAndGroups(objectIds: string[], queryType: PrincipalType): Promise<IPersonaProps[]> {
    if (objectIds.length === 0) return [];

    try {
        const promises = objectIds.map(async (objId) => {
            let oData: any;

            switch (queryType) {
                case PrincipalType.Group:
                    oData = await getGroupInfoById(objId);
                    break;
                case PrincipalType.People:
                    oData = await getUserInfoById(objId);
                    break;
                default:
                    oData = objId.includes("-")
                        ? await getGroupInfoById(objId)
                        : await getUserInfoById(objId);
                    break;
            }
            return {
                text: oData?.displayName || objId,
                secondaryText: oData?.userPrincipalName || objId,
                tertiaryText: oData?.id,
                showSecondaryText: oData?.id ? true : false,
            } as IPersonaProps;
        });

        const results = await Promise.all(promises);
        return results;

    } catch (error) {
        console.error("Error in queryPeopleAndGroups:", error);
        return [];
    }
}


async function searchPeople(filterText: string) {

    try {
        const isGuid = Helper.isValidGuid(filterText);
        const user = !!isGuid && await getUserInfoById(filterText);
        if (!!user) {
            return [user];
        }
        let response = await Providers.globalProvider.graph.client
            .api("/users")
            .filter(
                `startsWith(displayName, '${filterText}') or startsWith(userPrincipalName, '${filterText}') or startsWith(mail, '${filterText}')`
            )
            .get()
        return response.value;
    } catch (error) {
        console.error(error);
        return undefined
    }
}

export async function getGroupsByName(groupName: string) {
    try {
        let response = await Providers.globalProvider.graph.client.api("/groups")
            .count(true)
            .filter(`startsWith( displayName, '${encodeURIComponent(groupName)}')`)
            .get()
        return response.value;
    } catch (error) {
        console.error(error);
        return undefined
    }
}

export async function getMembersOfGroup(groupId: string) {
    try {
        let response = await Providers.globalProvider.graph.client.api(`/groups/${groupId}/members`)
            .get();

        return response.value;
    } catch (error) {
        console.error(error);
        return undefined
    }
}

export async function getGroupMemberCount(groupId: string): Promise<number> {
    try {
        let response = await Providers.globalProvider.graph.client
            .api(`/groups/${groupId}/members/$count`)
            .header('ConsistencyLevel', 'eventual')
            .get();

        return parseInt(response.toString(), 10) || 0;
    } catch (error) {
        console.error("Error getting group member count:", error);
        try {
            let membersResponse = await Providers.globalProvider.graph.client
                .api(`/groups/${groupId}/members`)
                .get();
            return membersResponse.value ? membersResponse.value.length : 0;
        } catch (fallbackError) {
            console.error("Fallback error:", fallbackError);
            return 0;
        }
    }
}

export async function checkUserMembershipInGroup(groupId: string, searchText: string): Promise<boolean> {
    try {
        // First, try to get the user by the search text (could be UPN, email, or ID)
        let user: any = null;

        // Check if searchText is a GUID (user ID)
        const isGuid = Helper.isValidGuid(searchText);

        if (isGuid) {
            // If it's a GUID, try to get user by ID
            try {
                user = await Providers.globalProvider.graph.client
                    .api(`/users/${searchText}`)
                    .get();
            } catch (error) {
                console.error("User not found by ID:", error);
                return false;
            }
        } else {
            // If it's not a GUID, assume it's UPN/email and add domain if needed
            const userIdentifier = addAliasDomain(searchText);

            try {
                user = await Providers.globalProvider.graph.client
                    .api(`/users/${userIdentifier}`)
                    .get();
            } catch (error) {
                // If direct lookup fails, try searching by display name or email
                try {
                    const searchResponse = await Providers.globalProvider.graph.client
                        .api("/users")
                        .filter(
                            `displayName eq '${searchText}' or userPrincipalName eq '${userIdentifier}' or mail eq '${userIdentifier}'`
                        )
                        .get();

                    if (searchResponse.value && searchResponse.value.length > 0) {
                        user = searchResponse.value[0];
                    } else {
                        console.warn("User not found by search:", searchText);
                        return false;
                    }
                } catch (searchError) {
                    console.error("Error searching for user:", searchError);
                    return false;
                }
            }
        }

        if (!user || !user.id) {
            console.error("No valid user found for:", searchText);
            return false;
        }

        // Now check if this user is a member of the group
        try {
            // Method 1: Direct membership check
            const membershipResponse = await Providers.globalProvider.graph.client
                .api(`/groups/${groupId}/members/${user.id}`)
                .get();

            return !!membershipResponse;
        } catch (membershipError: any) {
            // If direct check fails (404 means not a member), try alternative method
            if (membershipError.code === 'Request_ResourceNotFound' ||
                membershipError.status === 404) {
                return false;
            }

            // Method 2: Use checkMemberGroups API (more reliable for large groups)
            try {
                const checkResponse = await Providers.globalProvider.graph.client
                    .api(`/users/${user.id}/checkMemberGroups`)
                    .post({
                        groupIds: [groupId]
                    });

                return checkResponse.value && checkResponse.value.includes(groupId);
            } catch (checkError) {
                console.error("Error in checkMemberGroups:", checkError);
                return false;
            }
        }

    } catch (error) {
        console.error("Error in checkUserMembershipInGroup:", error);
        return false;
    }
}

async function searchGroups(filterText: string, isSecurityGroup: string = "true") {

    try {
        const isGuid = Helper.isValidGuid(filterText);
        const group = !!isGuid && await getGroupInfoById(filterText);
        if (!!group) {
            return [group];
        }
        let response = await Providers.globalProvider.graph.client.api("/groups")
            .count(true)
            .filter(`startsWith( displayName, '${filterText}') and securityEnabled eq ${isSecurityGroup}`)
            .get()
        return response.value;
    } catch (error) {
        console.error(error);
        return undefined
    }
}


export { getMe, checkAmInGroups, getGroupInfoById, searchGroups, searchPeople, queryPeopleAndGroups, getUserInfoById }